import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FolderOpen } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AssetCard } from "@/components/marketing/AssetCard";
import { PersonalizeDialog } from "@/components/marketing/PersonalizeDialog";
import { EmailTemplateDialog } from "@/components/marketing/EmailTemplateDialog";
import { MailingLabelsDialog } from "@/components/labels/MailingLabelsDialog";
import { Tags, Mailbox } from "lucide-react";
import { SECTION_META } from "@/lib/marketing";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function MarketingAssets() {
  const [sections, setSections] = useState(null);
  const [personalizing, setPersonalizing] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [labelsOpen, setLabelsOpen] = useState(false);
  useEffect(() => {
    api.get("/marketing/assets").then(({ data }) => setSections(data.sections)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    document.body.dataset.assistantDock = "header";
    window.dispatchEvent(new Event("assistant-dock"));
    return () => { delete document.body.dataset.assistantDock; window.dispatchEvent(new Event("assistant-dock")); };
  }, []);

  if (!sections) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  const total = sections.reduce((n, s) => n + s.assets.length, 0);
  const ready = sections.reduce((n, s) => n + s.assets.filter((a) => a.file_url || a.email_body).length, 0);

  return (
    <div className="space-y-8" data-testid="marketing-assets">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div className="space-y-2 max-w-3xl">
          <div className="flex items-center gap-2 text-primary"><FolderOpen className="w-5 h-5" /><span className="text-xs font-mono uppercase tracking-widest">Resource library</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="marketing-title">Marketing Assets</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Access professionally prepared sales, marketing, and outreach materials designed to help you build relationships with local businesses and grow advertiser participation along your bike path.</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 w-full sm:w-auto sm:min-w-[220px]" data-testid="marketing-summary">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Available now</div>
          <div className="text-sm mt-1"><b className="tabular-nums" data-testid="marketing-ready-count">{ready} of {total}</b> resources ready to download</div>
        </div>
      </header>

      <Accordion type="multiple" defaultValue={[slug(sections[0]?.title || "")]} className="space-y-4">
        {sections.map((s) => {
          const M = SECTION_META[s.title];
          const Icon = M?.icon || FolderOpen;
          return (
            <AccordionItem key={s.title} value={slug(s.title)} className="rounded-2xl border border-border bg-card/60 px-4 sm:px-6" data-testid={`marketing-section-${slug(s.title)}`}>
              <AccordionTrigger className="hover:no-underline py-5" data-testid={`marketing-section-toggle-${slug(s.title)}`}>
                <div className="flex items-center gap-4 text-left">
                  <span className={`w-11 h-11 rounded-xl bg-gradient-to-br ${M?.tint || ""} flex items-center justify-center shrink-0`}><Icon className="w-5 h-5" /></span>
                  <span>
                    <span className="block font-heading text-lg font-bold">{s.title} <span className="text-xs font-mono text-muted-foreground ml-1">{s.assets.length}</span></span>
                    <span className="block text-sm text-muted-foreground font-normal">{s.description}</span>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                {s.assets.length === 0 ? <div className="text-sm text-muted-foreground py-4">No resources in this section yet.</div> : (
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 pt-1">
                    {s.assets.map((a, i) => <AssetCard key={a.id} asset={a} index={i} onPersonalize={setPersonalizing} onEmail={setEmailing} />)}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
        <AccordionItem value="direct-mail-tools" className="rounded-2xl border border-border bg-card/60 px-4 sm:px-6" data-testid="marketing-section-direct-mail-tools">
          <AccordionTrigger className="hover:no-underline py-5" data-testid="marketing-section-toggle-direct-mail-tools">
            <div className="flex items-center gap-4 text-left">
              <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500/25 to-emerald-500/10 text-teal-400 flex items-center justify-center shrink-0"><Mailbox className="w-5 h-5" /></span>
              <span>
                <span className="block font-heading text-lg font-bold">Direct Mail Tools</span>
                <span className="block text-sm text-muted-foreground font-normal">Print labels for your prospects and launch postcard and flyer campaigns.</span>
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-6">
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 pt-1">
              <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" data-testid="marketing-mailing-labels-card">
                <div className="aspect-[16/9] bg-gradient-to-br from-teal-500/25 to-emerald-500/10 text-teal-400 flex items-center justify-center"><Tags className="w-12 h-12" strokeWidth={1.5} /></div>
                <div className="p-4 flex flex-col flex-1 gap-3">
                  <div className="space-y-1 flex-1">
                    <div className="font-heading font-semibold">Generate Mailing Labels</div>
                    <p className="text-sm text-muted-foreground">Export your prospects to print-ready Avery labels, then apply them to Velopaths postcards and mailers.</p>
                  </div>
                  <button type="button" onClick={() => setLabelsOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold transition-transform duration-200 hover:scale-[1.02]" data-testid="marketing-open-mailing-labels"><Tags className="w-4 h-4" />Generate Mailing Labels</button>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <MailingLabelsDialog open={labelsOpen} onOpenChange={setLabelsOpen} />
      <PersonalizeDialog asset={personalizing} onClose={() => setPersonalizing(null)} />
      <EmailTemplateDialog asset={emailing} onClose={() => setEmailing(null)} />
    </div>
  );
}
